/**
 * undoToast — "Undo destructive portfolio action" helper.
 *
 * Round P108e (Feb 2026).
 *
 * Every destructive portfolio endpoint (reset-all, csv-import
 * overwrite, DELETE /portfolios/{id}) now returns an
 * `undo_snapshot_id` + `undo_kind` + `undo_portfolio_name` +
 * `undo_ttl_seconds`. This helper renders a sonner toast that lets
 * the user restore that exact snapshot with one click.
 *
 * CRITICAL: the snapshot is scoped strictly to the portfolio_id that
 * was captured at destruction time. Undo NEVER widens scope to
 * sibling portfolios — the backend enforces this.
 *
 * Implementation note (iter 122 test-report fix): sonner drops
 * unknown option keys, so `{ 'data-testid': ... }` inside the toast
 * options was a no-op. We now use `toast.custom((id) => <div
 * data-testid=... />)` so the testid actually lands on a real DOM
 * node.
 */
import React from 'react';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Show a sonner toast with an Undo action button.
 *
 * @param {object} response - full API response body containing
 *   `undo_snapshot_id`, `undo_kind`, `undo_portfolio_name`,
 *   `undo_ttl_seconds`.
 * @param {object} [opts]
 * @param {string} [opts.successMessage] - toast title (default:
 *   derived from `kind` + `portfolio_name`).
 * @param {Function} [opts.onUndone] - fires after a successful undo
 *   (use this to trigger a portfolio-changed event + close any modal).
 */
export function showUndoToast(response, opts = {}) {
  const snapId = response?.undo_snapshot_id;
  if (!snapId) return null;

  const kind = response.undo_kind || 'reset_scoped';
  const pfName = response.undo_portfolio_name || 'your portfolio';
  const ttlMs = Math.max(4000, (response.undo_ttl_seconds || 900) * 1000);
  // Toast lifetime is capped at 30s so users see the notice, but the
  // undo itself remains valid for the full 15-min TTL server-side.
  const toastDuration = Math.min(ttlMs, 30_000);

  const title =
    opts.successMessage ||
    (kind === 'reset_global'
      ? 'Portfolio reset complete'
      : kind === 'portfolio_delete'
      ? `Deleted "${pfName}"`
      : kind === 'csv_overwrite'
      ? `Imported into "${pfName}" (overwrite)`
      : `Wiped "${pfName}"`);

  const description =
    kind === 'reset_global'
      ? 'Everything user-wide was cleared. Click Undo within 15 minutes to restore.'
      : `Only "${pfName}" was affected — your other portfolios are untouched. Undo within 15 minutes.`;

  const handleUndoClick = async (toastId) => {
    try {
      const { data } = await axios.post(
        `${API}/portfolio/undo/${snapId}`,
        {},
        { withCredentials: true },
      );
      toast.success(
        `Restored "${data?.portfolio_name || pfName}"`,
        {
          description: `${data?.positions_restored || 0} position(s) and ${data?.transactions_restored || 0} transaction(s) put back exactly where they were.`,
        },
      );
      try { window.dispatchEvent(new Event('portfolio-changed')); } catch { /* SSR */ }
      try { window.dispatchEvent(new Event('cash-changed')); } catch { /* SSR */ }
      if (typeof opts.onUndone === 'function') {
        try { opts.onUndone(data); } catch { /* caller crash — non-fatal */ }
      }
      toast.dismiss(toastId);
    } catch (err) {
      // Round P108z (Feb 2026) — distinguish 410 (expired), 409 (at
      // cap) and generic failure. 409 dismisses the toast and asks
      // the user to use the persistent Undo pill in the
      // PortfolioSelector dropdown, which knows how to open the
      // ReplacePortfolioModal picker.
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail;
      if (status === 409 && typeof detail === 'object' && detail?.code === 'portfolio_cap_reached') {
        toast.error("You're at your portfolio cap", {
          description: `You already have ${detail.current_count}/${detail.max_portfolios} portfolios. Open the portfolio dropdown and click the "Undo delete" pill — it'll ask you which portfolio to replace.`,
          duration: 12_000,
        });
        toast.dismiss(toastId);
        return;
      }
      if (status === 410) {
        toast.error('Undo window expired (15-min limit)', {
          description: (typeof detail === 'string' ? detail : detail?.message) || 'Deleted data can no longer be restored automatically. Restore from a Portfolio Snapshot if you have one.',
          duration: 10_000,
        });
        return;
      }
      const msg = typeof detail === 'string' ? detail : detail?.message;
      toast.error('Undo failed', {
        description: msg || 'Something went wrong on our end. Try again, or open the portfolio dropdown to use the persistent Undo pill (valid for 15 min).',
      });
    }
  };

  return toast.custom(
    (toastId) => (
      <div
        data-testid={`undo-toast-${kind}`}
        data-undo-snapshot-id={snapId}
        role="status"
        className="relative flex items-start gap-3 rounded-lg border border-amber-400/40 bg-background text-foreground shadow-lg p-3 pr-9 min-w-[340px] max-w-md"
      >
        <div className="flex-1 min-w-0">
          <div className="text-sm font-semibold leading-snug pr-4">{title}</div>
          <div className="text-xs text-foreground/75 mt-1 leading-snug">
            {description}
          </div>
        </div>
        <button
          type="button"
          data-testid={`undo-toast-${kind}-action`}
          onClick={() => handleUndoClick(toastId)}
          className="shrink-0 self-center rounded-md border border-amber-500/60 bg-amber-500/10 hover:bg-amber-500/20 text-amber-700 dark:text-amber-300 text-xs font-semibold px-3 py-1.5 transition-colors"
        >
          Undo
        </button>
        {/* Round P108e-x (Feb 2026) — user requested an X so the
            toast can be dismissed at will (e.g. when running rapid
            .xlsx re-import tests they don't want a 15-min pile-up).
            Dismissing does NOT consume the snapshot — the Undo API is
            still valid for the full server-side TTL, just no longer
            surfaced in the UI. */}
        <button
          type="button"
          aria-label="Dismiss"
          data-testid={`undo-toast-${kind}-close`}
          onClick={() => toast.dismiss(toastId)}
          className="absolute top-1.5 right-1.5 w-6 h-6 flex items-center justify-center rounded-md text-foreground/50 hover:text-foreground hover:bg-muted/60 transition-colors"
        >
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <line x1="18" y1="6" x2="6" y2="18" />
            <line x1="6" y1="6" x2="18" y2="18" />
          </svg>
        </button>
      </div>
    ),
    { duration: toastDuration },
  );
}
